import { useTranslation } from 'next-i18next/pages';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { JobPosition } from '@pflegia/data-access';
import { ReactComponent as ArrowBack } from '@pflegia/ui/icons/arrow-back.svg';
import { JOBS, JOB_BOARD } from '@pflegia/ui/pflegia/constants/routes';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { usePreviousRouteStore } from '@pflegia/ui/stores/previous-route.store';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';
import { getCityFallback } from '@pflegia/util/functions/job-position/helpers';

import { useRouterHelper } from '@shared/hooks/use-router-helper';

interface BackLinkProps {
  jobPosition: JobPosition;
}

const BackLink: React.FC<BackLinkProps> = ({ jobPosition }) => {
  const { t } = useTranslation('jobDetails');
  const router = useRouter();
  const previousRoute = usePreviousRouteStore(state => state.previousRoute);
  const { isTransparentJobDetails } = useRouterHelper();
  const { translateJobTitle } = useJobTitle();
  const isClientNavigation = useMemo(() => Boolean(previousRoute), [previousRoute]);

  const backLinkUrl = useMemo(() => {
    const location = getCityFallback(jobPosition.geoLocation);
    const jobTitle = jobPosition?.jobTitles?.[0]
      ? translateJobTitle({
          title: jobPosition.jobTitles[0].title,
          deputy: jobPosition.jobTitles[0].deputy ?? false,
        })
      : '';

    const jobListingsBase = isTransparentJobDetails ? JOBS : JOB_BOARD;
    const citySegment = location ? `${normalizeJobsParams(location)}` : '';
    const jobTitleSegment = jobTitle ? `${normalizeJobsParams(jobTitle)}` : '';

    const queryUrl = [jobTitleSegment, citySegment]
      .filter(Boolean)
      .map(segment => `/${segment}`)
      .join('');

    const queryString = queryUrl.toString().toLocaleLowerCase();
    return queryString ? `${jobListingsBase}${queryString}` : jobListingsBase;
  }, [jobPosition, translateJobTitle, isTransparentJobDetails]);

  const shouldUsePreviousRoute = useMemo(
    () => isClientNavigation && previousRoute && [JOBS, JOB_BOARD].some(route => previousRoute.startsWith(route)),
    [isClientNavigation, previousRoute],
  );

  const linkText = isClientNavigation ? t('backLink.backToJobBoard') : t('backLink.toJobBoard');

  return (
    <Link
      href={shouldUsePreviousRoute ? previousRoute || backLinkUrl : backLinkUrl}
      className='hover:text-primary-dark text-primary mb-4 inline-flex items-center gap-2 transition-colors duration-200 max-sm:mt-4'
      onClick={event => {
        event.preventDefault();
        shouldUsePreviousRoute ? router.push(JOBS) : router.push(backLinkUrl);
      }}
      aria-label={linkText}>
      <ArrowBack className='h-6 w-6' />
      <span className='text-base font-medium'>{linkText}</span>
    </Link>
  );
};

export default BackLink;
